/**
 * Runtime environment config, loaded from /env.js (window.__ENV__).
 * One build artifact serves all environments — the deploy workflow uploads
 * an environment-specific env.js next to the build (no-cache).
 */
export interface AppEnv {
  apiBaseUrl: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  /** Cognito hosted-UI domain, e.g. https://dnm-ops-dev.auth.ap-south-1.amazoncognito.com */
  cognitoDomain: string;
  /** OAuth redirect (callback) URI registered on the app client, e.g. https://ops.example/auth/callback */
  oauthRedirectUri: string;
}

/**
 * Shape of window.__ENV__. The contract names the new keys COGNITO_DOMAIN and
 * OAUTH_REDIRECT_URI; the existing keys are camelCase, so both spellings are
 * accepted (camelCase wins when both are present).
 */
export type RuntimeEnv = Partial<AppEnv> & {
  COGNITO_DOMAIN?: string;
  OAUTH_REDIRECT_URI?: string;
};

declare global {
  interface Window {
    __ENV__?: RuntimeEnv;
  }
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function currentOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';
}

/** Builds the effective config from a runtime object (pure — exported for tests). */
export function loadEnv(runtime: RuntimeEnv = {}, origin: string = currentOrigin()): AppEnv {
  const domain = runtime.cognitoDomain ?? runtime.COGNITO_DOMAIN ?? '';
  const redirect = runtime.oauthRedirectUri ?? runtime.OAUTH_REDIRECT_URI ?? '';
  return {
    apiBaseUrl: runtime.apiBaseUrl ?? 'http://localhost:8080/api/v1',
    cognitoUserPoolId: runtime.cognitoUserPoolId ?? '',
    cognitoClientId: runtime.cognitoClientId ?? '',
    cognitoDomain: stripTrailingSlash(domain.trim()),
    oauthRedirectUri: redirect.trim() || `${origin}/auth/callback`,
  };
}

function isSecureUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol === 'https:') return true;
    // Plain http is only acceptable for local development.
    return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
  } catch {
    return false;
  }
}

/**
 * Returns human-readable problems with the sign-in config (empty = OK). The
 * login page surfaces these instead of redirecting to a broken hosted UI.
 */
export function validateEnv(e: AppEnv): string[] {
  const problems: string[] = [];
  if (!e.cognitoClientId) problems.push('cognitoClientId is not set');
  if (!e.cognitoDomain) problems.push('COGNITO_DOMAIN is not set');
  else if (!isSecureUrl(e.cognitoDomain)) problems.push('COGNITO_DOMAIN must be an https:// URL');
  if (!isSecureUrl(e.oauthRedirectUri)) {
    problems.push('OAUTH_REDIRECT_URI must be an https:// URL');
  } else if (!new URL(e.oauthRedirectUri).pathname.endsWith('/auth/callback')) {
    problems.push('OAUTH_REDIRECT_URI must point at /auth/callback');
  }
  return problems;
}

const runtime: RuntimeEnv = (typeof window !== 'undefined' && window.__ENV__) || {};

export const env: AppEnv = loadEnv(runtime);
